import * as React from "react";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { Portal } from "react-portal";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import styles from "./Popin.module.less";

import { store, show, close, closeLast, overlayId } from "./PopinManager";
import PopinFooter from "./PopinFooter";

type PopinProps = {
  id: string;
  title?: React.ReactNode | string;
  closeableByExternalEvents?: boolean;
  onClose?: () => void;
  className?: string;
  returnFocus?: boolean;
  showOnMount?: boolean;
  width?: "narrow" | "normal" | "wide" | "extraWide";
  disableInitialFocus?: boolean;
  additionalButtons?: React.ReactNode;
  children?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<typeof PopinFooter>, "popinId" | "className">;

const Popin: React.FC<PopinProps> = (props) => {
  const id = useId(props.id);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const {
    showOnMount,
    onClose,
    title,
    className,
    returnFocus = true,
    disableInitialFocus,
    closeableByExternalEvents = true,
    additionalButtons,
    children,
    width,
  } = props;

  const lastEventTarget = React.useRef<EventTarget>();

  React.useEffect(() => {
    const unsub = store.subscribe((state, prevState) => {
      if (state.popins.includes(id) && !prevState.popins.includes(id)) {
        setIsClosing(false);
        setIsVisible(true);
      }

      if (!state.popins.includes(id) && prevState.popins.includes(id)) {
        setIsClosing(true);
      }
    });

    return () => {
      close(id);
      unsub();
    };
  }, [id]);

  React.useEffect(() => {
    if (showOnMount) {
      show(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.defaultPrevented) {
        if (event.key === "Escape") {
          if (closeableByExternalEvents) {
            closeLast(id);
          }
        }
      }
    };

    const onMouseDown = (event: MouseEvent | TouchEvent) => {
      if (event.defaultPrevented || event.target === lastEventTarget.current || (event instanceof MouseEvent && event.button !== 0)) {
        return;
      }
      if (closeableByExternalEvents) {
        closeLast(id);
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      onMouseDown(event);
    };

    if (isVisible) {
      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("mousedown", onMouseDown);
      document.addEventListener("touchstart", onTouchStart);

      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("mousedown", onMouseDown);
        document.removeEventListener("touchstart", onTouchStart);
      };
    }

    return () => null;
  }, [id, isVisible, closeableByExternalEvents]);

  const onAnimationEnd = () => {
    if (isClosing) {
      if (onClose) {
        onClose();
      }
      setIsClosing(false);
      setIsVisible(false);
    }
  };

  const popinClass = cls(
    {
      [styles.popin]: true,
      [styles.popinNarrow]: width === "narrow",
      [styles.popinWide]: width === "wide",
      [styles.popinExtraWide]: width === "extraWide",
      [styles.popinSlideIn]: isVisible,
      [styles.popinSlideOut]: isClosing,
    },
    className
  );

  return isVisible ? (
    <Portal node={document && document.getElementById(overlayId)}>
      <RemoveScroll removeScrollBar={false}>
        <FocusLock
          returnFocus={returnFocus}
          autoFocus={!disableInitialFocus}
          lockProps={{
            onMouseDown: (e: React.MouseEvent) => {
              lastEventTarget.current = e.target;
            },
            onTouchStart: (e: React.TouchEvent) => {
              lastEventTarget.current = e.target;
            },
          }}
        >
          <div id={`popin-${id}`} role="dialog" className={popinClass} aria-labelledby={`${id}-title`} onAnimationEnd={onAnimationEnd}>
            <header>
              <h1 id={`${id}-title`} className={styles.popinHeader}>
                {title}
              </h1>
            </header>
            {children}
            <PopinFooter popinId={id} {...props}>
              {additionalButtons}
            </PopinFooter>
          </div>
        </FocusLock>
      </RemoveScroll>
    </Portal>
  ) : null;
};

export default Popin;
